/**
 * Build-time feature flags (REACT_APP_FEATURE_FLAG_* === "true").
 *
 * GMA-1213: each flag emits a one-shot Sentry breadcrumb + tag on first
 * evaluation so we can prove from production Sentry which environment
 * built with which flag value (and catch silent build-time drift like
 * "prod accidentally shipped with Pontosense on").
 *
 * Breadcrumb category:  feature_flag
 * Sentry tag prefix:    flag.
 * Search Sentry:        tag:flag.pontosense_enabled:true
 */
import * as Sentry from "@sentry/react";

const reported = new Set();

const reportOnce = (flagName, value) => {
  if (reported.has(flagName)) return;
  reported.add(flagName);
  try {
    Sentry.addBreadcrumb({
      category: "feature_flag",
      level: "info",
      message: `flag.${flagName}=${value}`,
      data: { flag: flagName, value },
    });
    Sentry.setTag(`flag.${flagName}`, String(value));
  } catch (_ignored) {
    // Sentry not initialised (tests, local dev) — swallow.
  }
};

export const isPontosenseEnabled = () => {
  const enabled =
    process.env.REACT_APP_FEATURE_FLAG_PONTOSENSE_ENABLED === "true";
  reportOnce("pontosense_enabled", enabled);
  return enabled;
};

/**
 * GMA-1995 — draw/save elopement boundary on Command Center /web/map only.
 *
 * Set by scripts/build-commandcenter.sh (commandcenter.compasscare.app).
 * Capistrano stg2/prod builds must leave this unset so promotion to main
 * cannot ship draw/save before leave-area QA signs off.
 */
export const isElopementBoundaryEnabled = () => {
  const enabled =
    process.env.REACT_APP_FEATURE_FLAG_ELOPEMENT_BOUNDARY_ENABLED === "true";
  reportOnce("elopement_boundary_enabled", enabled);
  return enabled;
};

// Test-only escape hatch — DO NOT call from product code.
export const __resetFeatureFlagReporters = () => {
  reported.clear();
};
